import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { API_URL } from '../api';
import { useAuth } from '../context/AuthContext';
import RepoMembers from '../components/RepoMembers';
import '../components/RepoMembers.css';
import './Dashboard.css';

// Maps forge_init_pr_state (+ forge_initialized) to a semaphore-style status.
// Mirrors the green/amber/red states from the original Forge prototype:
// green = governance live, amber = pending review, red = needs attention.
// github_missing overrides everything else -- a cached "governed" status
// for a repo that's actually gone from GitHub would be exactly the kind
// of silent drift Forge exists to prevent.
function getRepoStatus(repo) {
  if (repo.github_missing) {
    return { label: 'Missing on GitHub', color: 'red' };
  }
  switch (repo.forge_init_pr_state) {
    case 'merged':
      return { label: 'Governed', color: 'green' };
    case 'open':
      return { label: 'Pending review', color: 'amber' };
    case 'closed':
      return { label: 'Init rejected', color: 'red' };
    default:
      return repo.forge_initialized
        ? { label: 'Governed', color: 'green' }
        : { label: 'Not initialized', color: 'steel' };
  }
}

// Flattens the detected_stack JSON blob into a short list of display chips.
function stackChips(stack) {
  if (!stack) return [];
  const chips = [];
  if (stack.primary_language) chips.push(stack.primary_language);
  (stack.frameworks || []).forEach((f) => chips.push(f));
  if (stack.containerized) chips.push('Docker');
  if (stack.ci) chips.push(stack.ci);
  return chips;
}

function Dashboard() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id;
  const username = user?.username;
  const reconnected = searchParams.get('reconnected');

  const [repoName, setRepoName] = useState('');
  const [repoDesc, setRepoDesc] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [organization, setOrganization] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [repos, setRepos] = useState([]);
  const [toast, setToast] = useState(null);

  // Browse / import existing GitHub repos
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseRepos, setBrowseRepos] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseError, setBrowseError] = useState(null);
  const [browseFilter, setBrowseFilter] = useState('');
  const [importingId, setImportingId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [applyingRulesId, setApplyingRulesId] = useState(null);
  const [unlinkingId, setUnlinkingId] = useState(null);
  const [membersOpenId, setMembersOpenId] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchRepos();
      fetchOrgs();
    }
  }, [userId]);

  // Show a one-time toast after a GitHub reconnect, then strip the query
  // param so refreshing the page doesn't keep re-showing it.
  useEffect(() => {
    if (reconnected === '1') {
      setToast({ text: `GitHub reconnected as @${username}`, type: 'success' });
      const timer = setTimeout(() => setToast(null), 4000);

      const params = new URLSearchParams(searchParams);
      params.delete('reconnected');
      navigate({ search: params.toString() }, { replace: true });

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line
  }, [reconnected]);

  const fetchRepos = async () => {
    try {
      const res = await fetch(`${API_URL}/api/repos/?user_id=${userId}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setRepos(data);
      }
    } catch (err) {
      // Keep the dashboard usable when the API is not running locally.
    }
  };

  const fetchOrgs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/repos/orgs?user_id=${userId}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setOrgs(data);
        if (data.length > 0 && !organization) {
          setOrganization(data[0].login);
        }
      }
    } catch (err) {
      // Fallback: leave orgs empty, user can still type manually
    }
  };

  // Fetched on demand (not on page load) -- this call paginates through
  // every repo the GitHub App installation can see, which can be a couple
  // hundred repos for an active org. No point paying that cost until the
  // user actually wants to browse/import.
  const fetchBrowseRepos = async () => {
    setBrowseLoading(true);
    setBrowseError(null);
    try {
      const res = await fetch(`${API_URL}/api/repos/github?user_id=${userId}`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        setBrowseRepos(data);
      } else {
        setBrowseError(data.detail || 'Failed to load GitHub repositories');
      }
    } catch (err) {
      setBrowseError('Network error — is the backend running?');
    } finally {
      setBrowseLoading(false);
    }
  };

  const toggleBrowse = () => {
    const next = !browseOpen;
    setBrowseOpen(next);
    if (next && browseRepos.length === 0) {
      fetchBrowseRepos();
    }
  };

  const handleImport = async (githubRepoId) => {
    setImportingId(githubRepoId);
    try {
      const res = await fetch(`${API_URL}/api/repos/import?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ github_repo_id: githubRepoId, init_forge: true }),
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok) {
        setToast({ text: `Imported "${data.full_name}" into Forge`, type: 'success' });
        setTimeout(() => setToast(null), 4000);
        setBrowseRepos((prev) =>
          prev.map((r) =>
            r.github_repo_id === githubRepoId ? { ...r, already_in_forge: true } : r
          )
        );
        fetchRepos();
      } else {
        setToast({ text: data.detail || 'Failed to import repository', type: 'error' });
        setTimeout(() => setToast(null), 4000);
      }
    } catch (err) {
      setToast({ text: 'Network error — is the backend running?', type: 'error' });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setImportingId(null);
    }
  };

  const handleSync = async (repoId) => {
    setSyncingId(repoId);
    try {
      const res = await fetch(`${API_URL}/api/repos/${repoId}/sync?user_id=${userId}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok) {
        setToast({ text: data.note, type: data.github_missing ? 'error' : 'success' });
        setTimeout(() => setToast(null), 4000);
        fetchRepos();
      } else {
        setToast({ text: data.detail || 'Sync failed', type: 'error' });
        setTimeout(() => setToast(null), 4000);
      }
    } catch (err) {
      setToast({ text: 'Network error — is the backend running?', type: 'error' });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSyncingId(null);
    }
  };

  const handleApplyRules = async (repoId) => {
    setApplyingRulesId(repoId);
    try {
      const res = await fetch(`${API_URL}/api/repos/${repoId}/apply-rules?user_id=${userId}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok) {
        // applied=false here isn't a request failure -- it's usually the
        // GitHub Free private-repo plan limitation, which the backend
        // already distinguishes from a real error. Surface the note
        // either way so the user knows why.
        setToast({ text: data.note, type: data.applied ? 'success' : 'error' });
        setTimeout(() => setToast(null), 6000);
        fetchRepos();
      } else {
        setToast({ text: data.detail || 'Failed to apply repository rules', type: 'error' });
        setTimeout(() => setToast(null), 6000);
      }
    } catch (err) {
      setToast({ text: 'Network error — is the backend running?', type: 'error' });
      setTimeout(() => setToast(null), 6000);
    } finally {
      setApplyingRulesId(null);
    }
  };

  const handleUnlink = async (repoId, fullName) => {
    if (!window.confirm(`Unlink "${fullName}" from Forge?\n\nThis removes it from Forge's database only — the GitHub repository itself is not affected.`)) {
      return;
    }
    setUnlinkingId(repoId);
    try {
      const res = await fetch(`${API_URL}/api/repos/${repoId}?user_id=${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok) {
        setToast({ text: data.note, type: 'success' });
        setTimeout(() => setToast(null), 4000);
        fetchRepos();
      } else {
        setToast({ text: data.detail || 'Failed to unlink repository', type: 'error' });
        setTimeout(() => setToast(null), 4000);
      }
    } catch (err) {
      setToast({ text: 'Network error — is the backend running?', type: 'error' });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setUnlinkingId(null);
    }
  };

  const handleCreateRepo = async (e) => {
    e.preventDefault();
    if (!repoName.trim()) return;

    setLoading(true);
    setMessage(null);

    try {
      const res = await fetch(`${API_URL}/api/repos/create?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: repoName,
          description: repoDesc,
          private: isPrivate,
          auto_init: true,
          organization: organization || null,
        }),
        credentials: 'include',
      });

      const data = await res.json();

      if (res.ok) {
        setMessage({ type: 'success', text: `Repository "${data.full_name}" created successfully!` });
        setRepoName('');
        setRepoDesc('');
        setIsPrivate(false);
        setOrganization('NyxTech');
        fetchRepos();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to create repository' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error — is the backend running?' });
    } finally {
      setLoading(false);
    }
  };

  if (!userId) {
    return (
      <div className="dashboard">
        <div className="empty-state">
          <p>Not authenticated. <a href="/">Go back and connect with GitHub</a>.</p>
        </div>
      </div>
    );
  }

  const filteredBrowseRepos = browseRepos.filter((r) =>
    r.full_name.toLowerCase().includes(browseFilter.toLowerCase())
  );

  return (
    <div className="dashboard">
      {toast && (
        <div className={`toast ${toast.type}`} role="status">
          {toast.text}
        </div>
      )}

      <div className="forge-hero dashboard-hero">
        <div className="forge-section-eyebrow">Panel de control del método</div>
        <h1>Hola{username ? `, @${username}` : ''}. <span className="accent">Bienvenido a Forge.</span></h1>
        <p>
          Crea o importa repositorios, activa el bootstrap de gobierno y avanza hacia specs
          trazables con PRs y tareas de agentes.
        </p>
        <div className="forge-actions">
          <Link to="/board" className="forge-button">
            Spec Board
          </Link>
          <a href={`${API_URL}/api/auth/github/login`} className="forge-button">
            Reconnect GitHub
          </a>
          <button type="button" className="forge-button primary" onClick={toggleBrowse}>
            {browseOpen ? 'Ocultar repos' : 'Importar repo existente'}
          </button>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="card">
          <h2>Create New Repository</h2>
          <p>Create a GitHub repository governed by Forge specs.</p>

          {message && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleCreateRepo}>
            <div className="form-group">
              <label htmlFor="repo-org">Owner</label>
              <select
                id="repo-org"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
              >
                {orgs.length > 0 ? (
                  orgs.map((org) => (
                    <option key={org.login} value={org.login}>
                      {org.login}{org.type === 'User' ? ' (personal)' : ''}
                    </option>
                  ))
                ) : (
                  <option value="">Loading…</option>
                )}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="repo-name">Repository name *</label>
              <input
                id="repo-name"
                type="text"
                value={repoName}
                onChange={(e) => setRepoName(e.target.value)}
                placeholder="my-new-project"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="repo-desc">Description</label>
              <textarea
                id="repo-desc"
                value={repoDesc}
                onChange={(e) => setRepoDesc(e.target.value)}
                placeholder="A short description of the repository"
              />
            </div>

            <div className="form-group">
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="repo-private"
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                />
                <label htmlFor="repo-private">Private repository</label>
              </div>
            </div>

            <button type="submit" className="btn-create" disabled={loading || !repoName.trim()}>
              {loading ? 'Creating...' : 'Create Repository'}
            </button>
          </form>
        </div>

        <div className="card">
          <h2>Forge Repositories</h2>
          <p>Repositories created or imported through the Forge platform.</p>

          {repos.length === 0 ? (
            <div className="empty-state">
              No repositories yet. Create or import one to get started.
            </div>
          ) : (
            <ul className="repo-list">
              {repos.map((repo) => {
                const status = getRepoStatus(repo);
                const chips = stackChips(repo.detected_stack);
                return (
                  <li key={repo.id} className={`repo-card ${repo.github_missing ? 'is-missing' : ''}`}>
                    <div className="repo-card-top">
                      <a href={repo.html_url} target="_blank" rel="noopener noreferrer" className="repo-name">
                        {repo.full_name}
                      </a>
                      <div className="repo-card-actions">
                        <span className={`repo-badge ${repo.private ? 'private' : ''}`}>
                          {repo.private ? 'Private' : 'Public'}
                        </span>
                        <button
                          type="button"
                          className="btn-sync"
                          title="Re-check status against GitHub"
                          disabled={syncingId === repo.id}
                          onClick={() => handleSync(repo.id)}
                        >
                          {syncingId === repo.id ? 'Syncing…' : 'Sync'}
                        </button>
                      </div>
                    </div>
                    <div className="repo-card-meta">
                      <span className={`status-pill ${status.color}`}>
                        <span className="status-dot" />
                        {status.label}
                      </span>
                      <span className="source-tag">{repo.source === 'imported' ? 'Imported' : 'Created'}</span>
                      {repo.forge_init_pr_url && (
                        <a
                          href={repo.forge_init_pr_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="pr-link"
                        >
                          View bootstrap PR →
                        </a>
                      )}
                    </div>
                    <div className="specs-row">
                      {status.color === 'green' ? (
                        <Link
                          to={`/specs?repo_id=${repo.id}`}
                          className="btn-specs"
                        >
                          Specs & Agent Tasks →
                        </Link>
                      ) : (
                        <span className="specs-disabled-hint" title="Governance must be merged before specs can be approved and dispatched to agents">
                          Specs available once governance is merged
                        </span>
                      )}
                    </div>
                    {chips.length > 0 && (
                      <div className="chip-row">
                        {chips.map((c) => (
                          <span key={c} className="chip">{c}</span>
                        ))}
                      </div>
                    )}
                    <div className="rules-row">
                      {repo.rules_applied ? (
                        <span className="rules-tag applied" title={repo.rules_apply_note}>
                          🔒 Branch protection active
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="btn-apply-rules"
                          title={
                            repo.rules_apply_note ||
                            'Requires GitHub Pro/Team for private repos, or the repo must be public'
                          }
                          disabled={applyingRulesId === repo.id}
                          onClick={() => handleApplyRules(repo.id)}
                        >
                          {applyingRulesId === repo.id ? 'Applying…' : 'Apply Repository Rules'}
                        </button>
                      )}
                    </div>
                    <div className="unlink-row">
                      <button
                        type="button"
                        className="members-toggle"
                        onClick={() => setMembersOpenId(membersOpenId === repo.id ? null : repo.id)}
                      >
                        {membersOpenId === repo.id ? 'Hide Members' : 'Members'}
                      </button>
                      <button
                        type="button"
                        className="btn-unlink"
                        disabled={unlinkingId === repo.id}
                        onClick={() => handleUnlink(repo.id, repo.full_name)}
                      >
                        {unlinkingId === repo.id ? 'Removing…' : 'Unlink from Forge'}
                      </button>
                    </div>
                    {membersOpenId === repo.id && (
                      <RepoMembers repoId={repo.id} userId={userId} />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="card browse-card">
        <div className="browse-header" onClick={toggleBrowse}>
          <div>
            <h2>Browse GitHub Repositories</h2>
            <p>Bring an existing repository under Forge governance.</p>
          </div>
          <button type="button" className="btn-toggle">
            {browseOpen ? 'Hide' : 'Browse'}
          </button>
        </div>

        {browseOpen && (
          <div className="browse-body">
            {browseLoading && <div className="empty-state">Loading repositories…</div>}

            {browseError && <div className="message error">{browseError}</div>}

            {!browseLoading && !browseError && browseRepos.length > 0 && (
              <>
                <input
                  type="text"
                  className="browse-filter"
                  placeholder="Filter by name…"
                  value={browseFilter}
                  onChange={(e) => setBrowseFilter(e.target.value)}
                />
                <ul className="browse-list">
                  {filteredBrowseRepos.slice(0, 50).map((repo) => (
                    <li key={repo.github_repo_id} className="browse-item">
                      <div className="browse-item-info">
                        <a href={repo.html_url} target="_blank" rel="noopener noreferrer">
                          {repo.full_name}
                        </a>
                        <span className={`repo-badge ${repo.private ? 'private' : ''}`}>
                          {repo.private ? 'Private' : 'Public'}
                        </span>
                      </div>
                      {repo.already_in_forge ? (
                        <span className="already-tag">Already in Forge</span>
                      ) : (
                        <button
                          type="button"
                          className="btn-import"
                          disabled={importingId === repo.github_repo_id}
                          onClick={() => handleImport(repo.github_repo_id)}
                        >
                          {importingId === repo.github_repo_id ? 'Importing…' : 'Import'}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                {filteredBrowseRepos.length > 50 && (
                  <div className="browse-truncated">
                    Showing 50 of {filteredBrowseRepos.length} matches — refine your filter to see more.
                  </div>
                )}
              </>
            )}

            {!browseLoading && !browseError && browseRepos.length === 0 && (
              <div className="empty-state">No repositories found for this installation.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
